import { Button, Checkbox, Input, Link } from '@heroui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { HTTPError } from 'ky'
import { doTgLogin, getCurrentUserRoleInfo, getTgBotRole } from '#shared/lib/auth'
import { getFormFieldLogicProps } from '#shared/lib/forms'
import { showNotification } from '#shared/lib/notifications'
import { useExternalLinkProps, useFormFieldStyleProps } from '#shared/lib/ui'
import { v } from '#shared/lib/validation'
import { TG_TOKENS_QUERY_KEY } from '#shared/state/tg-tokens-query'
import usePreregisterForm from '../lib/use-preregister-form'
import CreateUserErrorResponse from '../model/create-user-error-response'
import PreregisterFormValue from '../model/preregister-form-value'
import useCreateUserMutation from '../state/use-create-user-mutation'
import styles from './tg-preregister.module.scss'

export default function TgPreregister() {
  const fieldStyleProps = useFormFieldStyleProps()
  const roleInfo = getCurrentUserRoleInfo()

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const createUserMutation = useCreateUserMutation({
    async onError(error) {
      if (error instanceof HTTPError) {
        const responseBody = (await error.response.json()) as CreateUserErrorResponse

        if (responseBody.errorSlug === 'username_taken') {
          form.setFieldMeta('username', meta => ({
            ...meta,
            errorMap: { onChange: 'Пользователь с таким именем уже существует' },
          }))

          return
        }

        if (responseBody.errorSlug === 'email_taken') {
          form.setFieldMeta('email', meta => ({
            ...meta,
            errorMap: { onChange: 'Пользователь с такой почтой уже существует' },
          }))

          return
        }
      }

      showNotification('Ошибка при создании аккаунта. Попробуйте ещё раз', 'danger')
    },
  })

  const form = usePreregisterForm({
    async onSubmit(value) {
      await createUserMutation.mutateAsync(value)
      await queryClient.invalidateQueries({ queryKey: TG_TOKENS_QUERY_KEY })
      await doTgLogin()

      const botRole = getTgBotRole()
      navigate({ to: '/' + botRole })
    },
  })

  const onFormSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    form.handleSubmit()
  }

  const tosLinkProps = useExternalLinkProps(import.meta.env.PUBLIC_TOS_URL)
  const privacyPolicyLinkProps = useExternalLinkProps(import.meta.env.PUBLIC_PRIVACY_POLICY_URL)
  const contractLinkProps = useExternalLinkProps(import.meta.env.PUBLIC_CONTRACT_URL)

  return (
    <form className={styles.root} onSubmit={onFormSubmit}>
      <h1 className={styles.title}>Создать аккаунт</h1>

      <div className={styles.fields}>
        <form.Field name="username" validators={v<PreregisterFormValue, 'username'>(value => !value && 'Введите имя')}>
          {field => <Input label="Имя пользователя" {...fieldStyleProps} {...getFormFieldLogicProps(field)} />}
        </form.Field>

        <form.Field name="email" validators={v<PreregisterFormValue, 'email'>(value => !value && 'Введите почту')}>
          {field => (
            <Input label="Электронная почта" type="email" {...fieldStyleProps} {...getFormFieldLogicProps(field)} />
          )}
        </form.Field>

        <form.Field name="isAgreedWithPrivacyPolicyAndToS">
          {field => (
            <Checkbox isSelected={field.state.value} onValueChange={field.handleChange}>
              Согласен с{' '}
              <Link className={styles.legalPaperLink} {...privacyPolicyLinkProps}>
                политикой конфиденциальности
              </Link>{' '}
              и{' '}
              <Link className={styles.legalPaperLink} {...tosLinkProps}>
                пользовательским соглашением
              </Link>
            </Checkbox>
          )}
        </form.Field>

        {roleInfo.role === 'client' && (
          <form.Field name="isAgreedWithTermsOfContract">
            {field => (
              <Checkbox isSelected={field.state.value} onValueChange={field.handleChange}>
                Согласен с{' '}
                <Link className={styles.legalPaperLink} {...contractLinkProps}>
                  условиями договора
                </Link>
              </Checkbox>
            )}
          </form.Field>
        )}
      </div>

      <form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
        {([canSubmit, isSubmitting]) => (
          <Button
            variant="solid"
            color="primary"
            size="lg"
            radius="lg"
            isDisabled={!canSubmit || createUserMutation.isSuccess}
            isLoading={isSubmitting}
            type="submit"
          >
            Продолжить
          </Button>
        )}
      </form.Subscribe>
    </form>
  )
}
